import { apiClient } from '../axios-instance'
import type { AxiosResponse } from 'axios'
import type { PaginatedResponse } from './users.service'

export interface Product {
  id: string
  merchant_id: string
  category_id: string
  name: string
  description: string
  sku: string
  brand?: string
  unit_of_measure: string
  specifications?: Record<string, any>
  images: {
    url: string
    is_primary: boolean
    alt_text?: string
  }[]
  pricing: {
    base_price: number
    currency: string
    tax_rate: number
    discount_percentage?: number
  }
  inventory: {
    stock_quantity: number
    low_stock_threshold: number
    is_in_stock: boolean
  }
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'inactive'
  approval_status?: {
    status: string
    reviewed_by?: string
    reviewed_at?: string
    rejection_reason?: string
  }
  created_at: string
  updated_at: string
}

export interface ProductFilters {
  search?: string
  merchant_id?: string
  category_id?: string
  status?: string
  min_price?: number
  max_price?: number
  in_stock?: boolean
  page?: number
  per_page?: number
}

export interface CreateProductRequest {
  merchant_id: string
  category_id: string
  name: string
  description: string
  sku: string
  brand?: string
  unit_of_measure: string
  specifications?: Record<string, any>
  pricing: Product['pricing']
  inventory: Product['inventory']
}

export interface UpdateProductRequest {
  name?: string
  description?: string
  category_id?: string
  pricing?: Partial<Product['pricing']>
  inventory?: Partial<Product['inventory']>
  status?: Product['status']
}

export const productService = {
  getProducts: async (filters?: ProductFilters): Promise<PaginatedResponse<Product>> => {
    const response: AxiosResponse<PaginatedResponse<Product>> = await apiClient.get(
      '/products',
      { params: filters }
    )
    return response.data
  },

  getProductById: async (id: string): Promise<Product> => {
    const response: AxiosResponse<Product> = await apiClient.get(`/products/${id}`)
    return response.data
  },

  createProduct: async (data: CreateProductRequest): Promise<Product> => {
    const response: AxiosResponse<Product> = await apiClient.post('/products', data)
    return response.data
  },

  updateProduct: async (id: string, data: UpdateProductRequest): Promise<Product> => {
    const response: AxiosResponse<Product> = await apiClient.put(`/products/${id}`, data)
    return response.data
  },

  deleteProduct: async (id: string): Promise<void> => {
    await apiClient.delete(`/products/${id}`)
  },

  approveProduct: async (id: string): Promise<Product> => {
    const response: AxiosResponse<Product> = await apiClient.post(`/products/${id}/approve`)
    return response.data
  },

  rejectProduct: async (id: string, reason: string): Promise<Product> => {
    const response: AxiosResponse<Product> = await apiClient.post(
      `/products/${id}/reject`,
      { reason }
    )
    return response.data
  },

  bulkUpdateStatus: async (ids: string[], status: Product['status']): Promise<void> => {
    await apiClient.post('/products/bulk-update-status', { ids, status })
  },

  uploadImage: async (id: string, file: File): Promise<Product> => {
    const formData = new FormData()
    formData.append('image', file)
    const response: AxiosResponse<Product> = await apiClient.post(
      `/products/${id}/images`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    return response.data
  },

  deleteImage: async (id: string, imageId: string): Promise<Product> => {
    const response: AxiosResponse<Product> = await apiClient.delete(
      `/products/${id}/images/${imageId}`
    )
    return response.data
  },
}
